import { defaultTab } from '@/store/offers-dashboard/types';
import { NamedRoutes, QueryKeys } from '@/types';
import {
  Button,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Stack,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faPlus, faSquareArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { TabIdStageMap, TabIdStageMapForAgent } from 'data';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { EntryPoint, WorkflowAccessLevel, Workflows } from 'types';
import { ApplicationState } from '../../../../store';
import { hasPerm, isPartnerRole, P } from '../../../../utilities/roles/permissions';
import { ApplicationDrawer } from '@/components/organisms/ApplicationDrawer';
import { UserRole } from '../types';
import { Tabs } from './tabs';
import { workflowOptions } from '@/utilities/helpers';
import { getPartnerWorkflowLogic } from '../helpers';
import { useTranslation } from "react-i18next";

// Import the ApplicationDrawer type props
interface ApplicationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  applicationData?: {
    id?: string;
    studentName: string;
    feeStatus: string;
    nationality: string;
    course: string;
    mode: string;
    entryPoint: EntryPoint;
    status: string;
  };
}

interface IDropdownOption {
  value: string;
  label: string;
}

export const LeftBar = () => {
    const { t } = useTranslation('tables');
  const userRole = useSelector(({ auth }: ApplicationState) => auth.role);
  const workflowAccess = useSelector(({ auth }: ApplicationState) => auth.currentUser?.workflowAccess);
  const canAccessApply = useSelector(({ config }: ApplicationState) => config?.applyEnabled === true);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isPartner = isPartnerRole(userRole);

  const [currentWorkflow, setCurrentWorkflow] = useState(Workflows.ADMISSIONS);
  const [isApplicationDrawerOpen, setIsApplicationDrawerOpen] = useState(false);
  const [drawerApplicationData, setDrawerApplicationData] =
    useState<ApplicationDrawerProps['applicationData']>(undefined);

  const isAgentOrPartnerUser = useMemo(
    () => [UserRole.Agent, UserRole.AgencyAdmin, UserRole.Partner].includes(userRole),
    [userRole],
  );

  const isPartnerUser = useMemo(() => isPartnerRole(userRole), [userRole]);

  // permissions based rules for allowing application creation, not role based
  const canCreateApplication = hasPerm(userRole, P.CREATE_APPLY_APPLICATION);

  const hasFullAccess = useMemo(() => workflowAccess === WorkflowAccessLevel.FullAccess, [workflowAccess]);

  const hadAdmissionsAccess = useMemo(() => workflowAccess === WorkflowAccessLevel.AdmissionsAccess, [workflowAccess]);

  const partnerLogic = useMemo(
    () => (isPartnerUser ? getPartnerWorkflowLogic(userRole, hasFullAccess, hadAdmissionsAccess, location) : undefined),
    [userRole, hasFullAccess, hadAdmissionsAccess, location],
  );

  const canAccessOffersWorkflow = canAccessApply || hasFullAccess || hadAdmissionsAccess;

  // determining the default tab based on role
  const filteredWorkflowOptions = useMemo(() => {
    switch (workflowAccess) {
      case WorkflowAccessLevel.AdmissionsAccess:
        return workflowOptions.filter(workflow => workflow.value === Workflows.ADMISSIONS);
      case WorkflowAccessLevel.CasAccess:
        return workflowOptions.filter(workflow => workflow.value === Workflows.CAS);
      case WorkflowAccessLevel.FullAccess:
      default:
        return workflowOptions;
    }
  }, [canAccessOffersWorkflow]);

  const [shouldShowWorkflow, setShouldShowWorkflow] = useState<boolean>(true);

  useEffect(() => {
    const hasPartnerWorkflowAccess = !isPartnerRole(userRole) || partnerLogic?.shouldShowDropdown;
    const workflowsToShow = filteredWorkflowOptions && filteredWorkflowOptions.length > 0;
    const hasWorkflowPerms = hasPerm(userRole, P.CHANGE_WORKFLOW);

    setShouldShowWorkflow(hasPartnerWorkflowAccess && workflowsToShow && hasWorkflowPerms);
  }, [userRole, partnerLogic, filteredWorkflowOptions]);

  // Switching workflows based on location
  useEffect(() => {
    if (location.pathname === NamedRoutes.ADMISSIONS_WORKFLOW) {
      setCurrentWorkflow(Workflows.ADMISSIONS);
    } else if (location.pathname === NamedRoutes.ROOT || location.pathname === '/') {
      setCurrentWorkflow(Workflows.CAS);
    }
  }, [location.pathname]);

  // Partner auto navigation logic
  useLayoutEffect(() => {
    if (!isPartner || !partnerLogic) return;

    const shouldNavigate =
      partnerLogic?.shouldAutoNavigate && partnerLogic?.targetPath && location.pathname !== partnerLogic?.targetPath;

    if (shouldNavigate) {
      const existingParams = new URLSearchParams(searchParams);
      navigate({
        pathname: partnerLogic?.targetPath,
        search: existingParams.toString(),
      });
    }
  }, [partnerLogic?.shouldAutoNavigate, partnerLogic?.targetPath, navigate, location.pathname, searchParams]);

  // Open drawer with default new application data
  const openApplicationDrawer = useCallback(() => {
    console.debug('Opening application drawer for new applicant');
    setDrawerApplicationData({
      id: undefined,
      studentName: 'New Applicant',
      feeStatus: 'To be determined',
      nationality: 'Unknown',
      course: 'Select course',
      mode: 'Full time',
      entryPoint: EntryPoint.Year1,
      status: 'Lead',
    });
    setIsApplicationDrawerOpen(true);
  }, []);

  // State to determine if agent can create applications
  const {
    isOpen: showRegisterButton,
    onOpen: onShowRegisterButton,
    onClose: onHideRegisterButton,
  } = useDisclosure({ defaultIsOpen: false });

  // Check for button visibility using the availableUniversities from the hook
  useEffect(() => {
    /**
     * ( Application creation button should be shown if the user is an agent/partner
     * <OR> has access to the offers workflow )
     * <AND> they have permission to create applications, based on the permissions stack
     */
    const shouldShowButton = (isAgentOrPartnerUser || canAccessOffersWorkflow) && canCreateApplication;
    if (!shouldShowButton) {
      console.warn(
        "Not showing 'Create applicant' button due to insufficient permissions or no universities available",
      );
    }

    if (shouldShowButton) {
      onShowRegisterButton();
    } else {
      onHideRegisterButton();
    }
  }, [isAgentOrPartnerUser, onShowRegisterButton, onHideRegisterButton, canCreateApplication, canAccessOffersWorkflow]);

  // Get sidebar collapsed state from localStorage
  const { isOpen } = useDisclosure({
    defaultIsOpen: JSON.parse(localStorage.getItem('sidebarIsOpen') || 'true'), // Default to true if not set
  });

  // Update localStorage whenever isOpen changes
  useEffect(() => {
    localStorage.setItem('sidebarIsOpen', JSON.stringify(isOpen));
  }, [isOpen]);

  const navigateToWorkflow = useCallback(
    e => {
      switch (e.target.textContent) {
        case Workflows.ADMISSIONS: {
          if (location.pathname === NamedRoutes.ADMISSIONS_WORKFLOW) return;
          const params = new URLSearchParams(searchParams);
          params.set(QueryKeys.PAGE, '1');
          params.delete(QueryKeys.CURRENT_TAB); // when navigating to admissions, reset to default tab
          params.delete(QueryKeys.STAGE);
          params.delete(QueryKeys.APPLICATION_STAGE);
          params.delete(QueryKeys.OFFER_STAGE);

          const currentTab = defaultTab;

          const statusMap: { offerStatuses: string[]; applicationStatuses: string[] } = isAgentOrPartnerUser
            ? TabIdStageMapForAgent[currentTab]
            : TabIdStageMap[currentTab];

          const { applicationStatuses, offerStatuses } = statusMap ?? {};

          for (const status of applicationStatuses ?? []) {
            params.append(QueryKeys.APPLICATION_STAGE, status);
          }

          for (const status of offerStatuses ?? []) {
            params.append(QueryKeys.OFFER_STAGE, status);
          }

          params.set(QueryKeys.CURRENT_TAB, currentTab);

          navigate({ pathname: NamedRoutes.ADMISSIONS_WORKFLOW, search: params.toString() });
          break;
        }
        case Workflows.CAS: {
          if (location.pathname === NamedRoutes.ROOT) return;
          navigate(NamedRoutes.ROOT);
          break;
        }
      }
    },
    [location.pathname, navigate, searchParams, isAgentOrPartnerUser],
  );

  const shouldShowWorkflowDropdown = filteredWorkflowOptions.length > 1;

  return (
    <>
      {shouldShowWorkflow && (
        <Stack spacing={0} pt={{ base: 0, md: 4 }}>
          {shouldShowWorkflowDropdown && (
            <Menu>
              <MenuButton
                as={Button}
                variant="secondary"
                textAlign="left"
                leftIcon={<FontAwesomeIcon className="utility-icon" icon={faSquareArrowRight} />}
                rightIcon={<FontAwesomeIcon className="utility-icon" icon={faAngleDown} />}
                data-selector="workflow-dropdown"
                mx="sidebarPxSmall"
              >
                <VisuallyHidden>{t('tables:workflow')} </VisuallyHidden>
                {currentWorkflow}
              </MenuButton>

              <MenuList minWidth={'180px'}>
                <MenuOptionGroup defaultValue={currentWorkflow} type="radio">
                  {filteredWorkflowOptions.map((option: IDropdownOption, index) => (
                    <MenuItemOption key={index} value={option.value} onClick={navigateToWorkflow}>
                      {option.label}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          )}

          {/* Create applicant button for agents */}

          {showRegisterButton && (
            <Button
              data-selector="create-applicant-button"
              colorScheme="blue"
              variant="secondary"
              size="sm"
              onClick={openApplicationDrawer}
              leftIcon={<FontAwesomeIcon className="utility-icon" icon={faPlus} />}
              mt={3}
              mx="sidebarPxSmall"
              textAlign="left"
              justifyContent="flex-start"
            >
              {t('tables:createApplicant')}
                                      </Button>
          )}
        </Stack>
      )}

      <Stack
        spacing={4}
        className="nav-scrollbar"
        overflowY={{ base: undefined, md: 'auto' }}
        px={'sidebarPxSmall'}
        py={6}
      >
        <Tabs />
      </Stack>

      <ApplicationDrawer
        isOpen={isApplicationDrawerOpen}
        onClose={() => setIsApplicationDrawerOpen(false)}
        applicationData={drawerApplicationData}
      />
    </>
  );
};
